import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import Modal from "../../components/AssetsComponent/Modal";
import { fetchProducts } from "../../store/slices/leadSlice";
import { useDispatch } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { getAllCustomers } from "../../store/slices/customerSlice";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import EpcCustomerSkeleton from "../../components/Skeleton/EpcCustomerSkeleton";
import toast from "react-hot-toast";
import { ICustomer } from "../../models/epcCustomer.model";
const ProductTypes = lazy(
  () => import("../../components/AssetsComponent/ProductTypes")
);

const EpcCustomer: React.FC = () => {
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [epcName, setEpcName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingState, setBillingState] = useState("");
  const [billingPincode, setBillingPincode] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingState, setShippingState] = useState("");

  const [shippingPincode, setShippingPincode] = useState("");
  const [gstin, setGstin] = useState("");
  const [error, setError] = useState("");
  const [, setTypes] = useState<{
    [key: string]: {
      gst: number;
      _id: string;
      price: number;
      quantity: number;
      count: number;
    };
  }>({});
  const [dateFilter, setDateFilter] = useState<"all" | "15" | "30">("all");
  const [copyAddress, setCopyAddress] = useState(false);
  const loading = useSelector((state: RootState) => state.customer.loading);
  const customers = useSelector((state: RootState) => state.customer.customers);
  const epc = useSelector(
    (state: RootState) => state.epc.epc as { name?: string; email?: string }
  );
  const dispatch: RootDispatch = useDispatch();
  const handleFetchProduct = async () => {
    try {
      const response = await dispatch(fetchProducts())?.unwrap();

      if (!response || typeof response !== "object") {
        console.error("fetchProducts returned an invalid response:", response);
        return;
      }

      const productsArray = Object.entries(response).map(
        ([name, item]: [string, any]) => ({
          name,
          ...item,
          count: item.quantity,
        })
      );

      const productsObject = productsArray.reduce((acc, product) => {
        acc[product.name] = product;
        return acc;
      });

      setTypes(productsObject);
    } catch (error: any) {
      toast.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    handleFetchProduct();
  }, []);

  const fetchCustomersEpc = async () => {
    await dispatch(getAllCustomers());
  };

  useEffect(() => {
    fetchCustomersEpc();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowModal(false);
        setError("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const filteredCustomers = useMemo(() => {
    const filterCustomersByDate = (
      customers: ICustomer[],
      filter: typeof dateFilter
    ) => {
      if (filter === "all") return customers;
      const days = filter === "15" ? 15 : 30;
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);

      return customers.filter(
        (customer) =>
          customer.lastOrderDate &&
          new Date(customer.lastOrderDate) >= cutoffDate
      );
    };

    return filterCustomersByDate(
      customers.filter((c) =>
        c.name?.toLowerCase().includes(search.toLowerCase())
      ),
      dateFilter
    );
  }, [customers, search, dateFilter]);

  const handleCheckboxChange = () => {
    const newCopyAddress = !copyAddress;
    setCopyAddress(newCopyAddress);

    if (newCopyAddress) {
      setShippingAddress(billingAddress);
      setShippingCity(billingCity);
      setShippingState(billingState);
      setShippingPincode(billingPincode);
    } else {
      setShippingAddress("");
      setShippingCity("");
      setShippingState("");
      setShippingPincode("");
    }
  };

  if (loading) {
    return <EpcCustomerSkeleton />;
  }

  return (
    <div className="p-4 px-4 md:px-6">
      <h2 className="text-lg font-semibold text-slate-800 flex items-center mb-4">
        <span className="w-1 h-6 bg-navyBlue-primary  mr-3 rounded-full" />
        Product Available
      </h2>

      <Suspense fallback={<EpcCustomerSkeleton />}>
        <ProductTypes />
      </Suspense>

      {/* Search & Add Button */}
      <div className="flex flex-col md:flex-row items-center gap-4 mb-6 w-full">
        {/* Search Input */}
        <div className="flex flex-col md:flex-row gap-4 w-full text-sm">
          <div className="relative flex-1 w-full">
            <input
              type="text"
              placeholder="Search by EPC name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full p-3 pl-10 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm6-2l4 4"
              />
            </svg>
          </div>
          <div className="w-full md:w-60">
            <select
              value={dateFilter}
              onChange={(e) =>
                setDateFilter(e.target.value as "all" | "15" | "30")
              }
              className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="all">All</option>
              <option value="15">Last 15 Days</option>
              <option value="30">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      <Modal
        isVisible={showModal}
        title="Enter Details"
        onClose={() => {
          setShowModal(false);
          setError("");
        }}
      >
        <div className="fixed inset-0 flex items-center justify-center p-4  bg-opacity-50 z-50 lg:text-sm text-xs">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl  max-h-[70vh] lg:max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                  <strong>Error:</strong> {error}
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      id: "epcName",
                      label: "EPC Name",
                      value: epcName,
                      setValue: setEpcName,
                    },
                    {
                      id: "email",
                      label: "Email",
                      type: "email",
                      value: email,
                      setValue: setEmail,
                    },
                    {
                      id: "mobileNumber",
                      label: "Mobile Number",
                      type: "tel",
                      value: mobileNumber,
                      setValue: setMobileNumber,
                    },
                    {
                      id: "GSTIN",
                      label: "GSTIN",
                      value: gstin,
                      setValue: setGstin,
                    },
                  ].map(({ id, label, value, setValue, type = "text" }) => (
                    <div key={id} className="flex flex-col">
                      <label
                        htmlFor={id}
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        {label}
                      </label>
                      <input
                        type={type}
                        id={id}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        className="w-full rounded-md border border-gray-300 p-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        placeholder="Enter EPC Name"
                      />
                    </div>
                  ))}
                </div>

                <div className="p-6 border border-gray-200 rounded-lg shadow-sm bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Billing Address
                  </h3>
                  <div className="space-y-4">
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Billing Address
                      </label>
                      <textarea
                        rows={3}
                        value={billingAddress}
                        onChange={(e) => setBillingAddress(e.target.value)}
                        className="w-full rounded-md border border-gray-300 p-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Enter Billing Address"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        {
                          label: "Billing City",
                          value: billingCity,
                          setValue: setBillingCity,
                        },
                        {
                          label: "Billing State",
                          value: billingState,
                          setValue: setBillingState,
                        },
                        {
                          label: "Billing Pincode",
                          value: billingPincode,
                          setValue: setBillingPincode,
                        },
                      ].map(({ label, value, setValue }, index) => (
                        <div key={index} className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {label}
                          </label>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            className="w-full rounded-md border border-gray-300 p-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={`Enter ${label}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="p-6 border border-gray-200 rounded-lg shadow-sm bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Shipping Address
                  </h3>
                  <div className="flex items-center gap-2 mb-4">
                    <input
                      type="checkbox"
                      checked={copyAddress}
                      onChange={handleCheckboxChange}
                      className="cursor-pointer"
                    />
                    <label className="text-sm text-gray-600">
                      Copy from Billing Address
                    </label>
                  </div>
                  <div className="space-y-4">
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Shipping Address
                      </label>
                      <textarea
                        rows={3}
                        value={shippingAddress}
                        onChange={(e) => setShippingAddress(e.target.value)}
                        className="w-full rounded-md border border-gray-300 p-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Enter Shipping Address"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        {
                          label: "Shipping City",
                          value: shippingCity,
                          setValue: setShippingCity,
                        },
                        {
                          label: "Shipping State",
                          value: shippingState,
                          setValue: setShippingState,
                        },
                        {
                          label: "Shipping Pincode",
                          value: shippingPincode,
                          setValue: setShippingPincode,
                        },
                      ].map(({ label, value, setValue }, index) => (
                        <div key={index} className="flex flex-col">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {label}
                          </label>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            className="w-full rounded-md border border-gray-300 p-2 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={`Enter ${label}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setError("");
                    }}
                    className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-all shadow-md"
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Modal>

      <div className="bg-white shadow-lg rounded-lg p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center mb-4">
          <span className="w-1 h-6 bg-navyBlue-primary mr-3 rounded-full" />
          EPC List
        </h2>
        <div className="overflow-x-auto">
          <table className="text-xs sm:text-sm w-full border-collapse rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-100 text-gray-600">
                <th className="px-4 py-3 text-left">EPC Name</th>
                <th className="px-4 sm:px-6 py-3 text-left">Email</th>
                <th className="px-4 sm:px-6 py-3 text-left">Mobile Number</th>
                <th className="px-4 sm:px-6 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map((epc: ICustomer) => (
                  <tr
                    key={epc._id}
                    className="border-b last:border-none hover:bg-gray-50 transition duration-200"
                  >
                    <td className="px-4 sm:px-6 py-4 text-gray-700">
                      {epc.name}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-gray-700">
                      {epc.email}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-gray-700">
                      {epc.mobileNumber}
                    </td>
                    <td className="px-4 sm:px-6 py-4 flex justify-center gap-2 sm:gap-4">
                      {customers
                        .filter((c) => c._id === epc._id)
                        .some((c) => c.canViewQuotation) && (
                        <Link
                          to={`/EpcCustomer/${epc._id}/customer-quotation/${epc.leadId?._id}`}
                        >
                          <button type="button">
                            <div className="flex items-center justify-center gap-1">
                              <p className="pl-4">Quotation</p>
                              <FileText className="w-5 h-5 text-gray-500" />
                            </div>
                          </button>
                        </Link>
                      )}

                      <div className="flex items-center gap-0.5">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 sm:h-5 sm:w-5 text-white"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3 sm:h-4 sm:w-4 text-white -ml-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center text-gray-500 p-6">
                    No matching EPCs found.
                  </td>
                </tr>
              )}
              {epc &&
                Array.isArray(epc) &&
                epc.length > 0 &&
                epc.map((customer) => (
                  <tr
                    key={customer._id}
                    className="border-b last:border-none hover:bg-gray-50 transition duration-200"
                  >
                    <td className="px-4 sm:px-6 py-4 text-gray-700">
                      {customer.name}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-gray-700">
                      {customer.email}
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-gray-700">
                      {customer.mobilenumber}
                    </td>
                    <td className="px-4 sm:px-6 py-4 flex justify-center gap-2 sm:gap-4">
                      {customer._id ? (
                        <Link
                          aria-label="Create-link"
                          to={`/EpcCustomer/${customer._id}/customer-quotation`}
                        >
                          <button type="button">
                            <div className="flex items-center justify-center gap-1">
                              <p className="pl-4">Quotation</p>
                              <p>
                                <FileText className="w-5 h-5 text-gray-500" />
                              </p>
                            </div>
                          </button>
                        </Link>
                      ) : (
                        <button
                          disabled
                          className="opacity-50 bg-gray-300 px-4 py-2 rounded"
                        >
                          Invalid EPC
                        </button>
                      )}

                      <div className="flex items-center gap-0.5">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 sm:h-5 sm:w-5 text-white"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3 sm:h-4 sm:w-4 text-white -ml-1"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EpcCustomer;
